/**
 * The station UI focus model (plan §6 M2-18): pure and tested. Every screen is a list of focus groups (tab bar,
 * lists, detail, footer). One item has the focus; inputs move it inside a group, jump between groups, adjust a
 * focused stepper, or ask the screen for an action (activate, back, previous/next tab, launch).
 *
 * Inputs come from the keyboard (arrows or WASD, Enter/Space, Backspace, Q/E, L; `keyToNavInput`) and the gamepad
 * (D-pad or left stick, A, B, LB/RB, Y; `stepUiPad`, built on the pause menu's `stepPadNav`). Esc is left to the
 * loop: it opens the pause menu over the station.
 *
 * `stepUiPad` runs every frame and is allocation-free (D-014); the rest runs on input events only.
 */
import type { MenuNavConfig } from '../settings/settings'
import { createPadNav, stepPadNav, type PadNavState } from '../hud/menu'
import type { PadLike } from '../input/playerInput'

export type NavInput = 'up' | 'down' | 'left' | 'right' | 'activate' | 'back' | 'prevTab' | 'nextTab' | 'launch'

/**
 * A focus group: `count` items laid out row-major in `cols` columns (1 = a vertical list; `count` = one row).
 * Up/down leave the group from its top/bottom row to `up`/`down` (default: the previous/next group in the list);
 * left/right leave it from its first/last column only through `left`/`right`. Items listed in `steppers` take
 * left/right as an adjustment instead of a move.
 */
export interface FocusGroup {
  id: string
  count: number
  /** Columns (default 1). */
  cols?: number
  up?: string
  down?: string
  left?: string
  right?: string
  /** Item indices that adjust a value with left/right. */
  steppers?: readonly number[]
}

export interface FocusState {
  group: string
  index: number
  /** The last focused index per group, so returning to a group lands where the player left it. */
  memory: Readonly<Record<string, number>>
}

export type NavResult =
  | { kind: 'none' }
  | { kind: 'moved' }
  | { kind: 'activate' }
  | { kind: 'adjust'; delta: 1 | -1 }
  | { kind: 'back' }
  | { kind: 'prevTab' }
  | { kind: 'nextTab' }
  | { kind: 'launch' }

export interface NavStep {
  focus: FocusState
  result: NavResult
}

const NONE: NavResult = { kind: 'none' }
const MOVED: NavResult = { kind: 'moved' }

/** The DOM key of a focus position (`data-nav` attribute). */
export function focusKey(group: string, index: number): string {
  return `${group}:${index}`
}

function colsOf(g: FocusGroup): number {
  const c = g.cols ?? 1
  return c >= 1 ? Math.floor(c) : 1
}

function clampIndex(g: FocusGroup, index: number): number {
  if (g.count <= 0) return 0
  return index < 0 ? 0 : index >= g.count ? g.count - 1 : index
}

function remember(focus: FocusState, group: string, index: number): FocusState {
  return { group, index, memory: { ...focus.memory, [group]: index } }
}

/**
 * A focus on the first item of the first non-empty group (or of `preferred` when it has items). `seed` pre-fills the
 * memory (the tab bar remembers the active tab, so moving up lands on it). `start` is the item the focus starts on
 * in `preferred` (CL-10: the title starts on New career when there is nothing to continue); it is clamped, and a
 * fallback group starts on its first item.
 */
export function initialFocus(groups: readonly FocusGroup[], preferred?: string, seed: Readonly<Record<string, number>> = {}, start = 0): FocusState {
  const pick = groups.find((g) => g.id === preferred && g.count > 0) ?? groups.find((g) => g.count > 0) ?? groups[0]
  const id = pick?.id ?? ''
  const index = pick !== undefined && pick.id === preferred ? clampIndex(pick, start) : 0
  return { group: id, index, memory: { ...seed, [id]: index } }
}

/**
 * Makes `focus` valid for `groups`: a focus on a vanished or empty group moves to the nearest non-empty group
 * (searching forward, then back), and an index past the end is clamped. Returns `focus` itself when valid.
 */
export function normalizeFocus(groups: readonly FocusGroup[], focus: FocusState): FocusState {
  const at = groups.findIndex((g) => g.id === focus.group)
  if (at >= 0 && groups[at].count > 0) {
    const index = clampIndex(groups[at], focus.index)
    return index === focus.index ? focus : remember(focus, focus.group, index)
  }
  const start = at >= 0 ? at : 0
  for (let d = 0; d < groups.length; d++) {
    const fwd = groups[start + d]
    if (fwd !== undefined && fwd.count > 0) return enter(focus, fwd)
    const back = groups[start - d]
    if (back !== undefined && back.count > 0) return enter(focus, back)
  }
  return focus
}

function enter(focus: FocusState, g: FocusGroup): FocusState {
  return remember(focus, g.id, clampIndex(g, focus.memory[g.id] ?? 0))
}

type Dir = 'up' | 'down' | 'left' | 'right'

/** The group a move in `dir` leaves `g` for (explicit link, else the list neighbour for up/down). */
function neighbour(groups: readonly FocusGroup[], g: FocusGroup, dir: Dir): FocusGroup | null {
  const link = g[dir]
  if (link !== undefined) return groups.find((x) => x.id === link) ?? null
  if (dir === 'left' || dir === 'right') return null
  const at = groups.indexOf(g)
  return groups[dir === 'up' ? at - 1 : at + 1] ?? null
}

/** Leaves `g` in `dir`, skipping empty groups along the same direction. */
function leave(groups: readonly FocusGroup[], focus: FocusState, g: FocusGroup, dir: Dir): FocusState | null {
  let next = neighbour(groups, g, dir)
  for (let hops = 0; next !== null && hops < groups.length; hops++) {
    if (next.count > 0) return enter(focus, next)
    next = neighbour(groups, next, dir)
  }
  return null
}

function move(groups: readonly FocusGroup[], focus: FocusState, g: FocusGroup, dir: Dir): NavStep {
  const cols = colsOf(g)
  const i = clampIndex(g, focus.index)
  const row = Math.floor(i / cols)
  const col = i % cols
  const lastRow = Math.floor((g.count - 1) / cols)
  let target = -1
  if (dir === 'up' && row > 0) target = i - cols
  else if (dir === 'down' && row < lastRow) target = Math.min(i + cols, g.count - 1)
  else if (dir === 'left' && col > 0) target = i - 1
  else if (dir === 'right' && col < cols - 1 && i + 1 < g.count) target = i + 1
  if (target >= 0) return { focus: remember(focus, g.id, target), result: MOVED }
  const out = leave(groups, focus, g, dir)
  return out === null ? { focus, result: NONE } : { focus: out, result: MOVED }
}

/** Applies one input to the focus. The focus is normalised first, so a stale focus never breaks navigation. */
export function navigate(groups: readonly FocusGroup[], current: FocusState, input: NavInput): NavStep {
  const focus = normalizeFocus(groups, current)
  const g = groups.find((x) => x.id === focus.group)
  switch (input) {
    case 'activate':
      return { focus, result: g !== undefined && g.count > 0 ? { kind: 'activate' } : NONE }
    case 'back':
    case 'prevTab':
    case 'nextTab':
    case 'launch':
      return { focus, result: { kind: input } }
    case 'left':
    case 'right':
      if (g !== undefined && g.steppers?.includes(focus.index) === true) {
        return { focus, result: { kind: 'adjust', delta: input === 'right' ? 1 : -1 } }
      }
      return g === undefined ? { focus, result: NONE } : move(groups, focus, g, input)
    case 'up':
    case 'down':
      return g === undefined ? { focus, result: NONE } : move(groups, focus, g, input)
  }
}

/** A focus on `index` of `group` (clamped), remembering it. */
export function focusAt(groups: readonly FocusGroup[], focus: FocusState, group: string, index: number): FocusState {
  const g = groups.find((x) => x.id === group)
  if (g === undefined || g.count <= 0) return focus
  const i = clampIndex(g, index)
  if (focus.group === group && focus.index === i) return focus
  return remember(focus, group, i)
}

/** Steps a tab list: the tab `delta` places from `current` (wrapping), or `current` when the list is empty. */
export function stepTab<T>(tabs: readonly T[], current: T, delta: 1 | -1): T {
  if (tabs.length === 0) return current
  const at = tabs.indexOf(current)
  const from = at < 0 ? 0 : at
  return tabs[(from + delta + tabs.length) % tabs.length]
}

/**
 * Keyboard `code` → UI input while a station or title screen is up (null = not a UI key; the key goes on to the
 * game). `repeat` = an auto-repeat keydown: directions repeat, actions do not.
 */
export function keyToNavInput(code: string, repeat: boolean): NavInput | null {
  switch (code) {
    case 'ArrowUp':
    case 'KeyW':
      return 'up'
    case 'ArrowDown':
    case 'KeyS':
      return 'down'
    case 'ArrowLeft':
    case 'KeyA':
      return 'left'
    case 'ArrowRight':
    case 'KeyD':
      return 'right'
    case 'Enter':
    case 'NumpadEnter':
    case 'Space':
      return repeat ? null : 'activate'
    case 'Backspace':
      return repeat ? null : 'back'
    case 'KeyQ':
      return repeat ? null : 'prevTab'
    case 'KeyE':
      return repeat ? null : 'nextTab'
    case 'KeyL':
      return repeat ? null : 'launch'
    default:
      return null
  }
}

/** Standard-mapping pad buttons the station UI adds to the pause menu's `PAD_NAV` (A, B, D-pad). */
export const UI_PAD = Object.freeze({ y: 3, lb: 4, rb: 5 })

export interface UiPadState {
  readonly nav: PadNavState
  prevY: boolean
  prevLb: boolean
  prevRb: boolean
}

/** `startButton` is the bindings' pause button (the pause edge itself stays with the loop). */
export function createUiPad(cfg: MenuNavConfig, startButton: number): UiPadState {
  return { nav: createPadNav(cfg, startButton), prevY: false, prevLb: false, prevRb: false }
}

function padDown(pad: PadLike | null, index: number): boolean {
  return pad !== null && pad.buttons[index]?.pressed === true
}

/**
 * Reads the pad once per frame: at most one UI input. D-pad, stick, A and B go through `stepPadNav` (edges plus
 * held-direction repeat); LB, RB and Y fire on their rising edge. The pad's Start is ignored here (the loop pauses
 * on it). Allocation-free: returns string constants or null.
 */
export function stepUiPad(state: UiPadState, pad: PadLike | null, dt: number): NavInput | null {
  const y = padDown(pad, UI_PAD.y)
  const lb = padDown(pad, UI_PAD.lb)
  const rb = padDown(pad, UI_PAD.rb)
  const yEdge = y && !state.prevY
  const lbEdge = lb && !state.prevLb
  const rbEdge = rb && !state.prevRb
  state.prevY = y
  state.prevLb = lb
  state.prevRb = rb
  const m = stepPadNav(state.nav, pad, dt)
  if (yEdge) return 'launch'
  if (lbEdge) return 'prevTab'
  if (rbEdge) return 'nextTab'
  if (m === null || m === 'start') return null
  return m
}

/** Carries inputs from the station feature (keyboard listener, pad polling) to the React tree, synchronously. */
export interface NavBus {
  /** Delivers `input` to every listener now (inside the key or pad event, so pointer lock can be requested). */
  dispatch(input: NavInput): void
  subscribe(listener: (input: NavInput) => void): () => void
}

export function createNavBus(): NavBus {
  const listeners: ((input: NavInput) => void)[] = []
  return {
    dispatch(input: NavInput): void {
      for (let i = 0; i < listeners.length; i++) listeners[i](input)
    },
    subscribe(listener: (input: NavInput) => void): () => void {
      listeners.push(listener)
      return (): void => {
        const i = listeners.indexOf(listener)
        if (i >= 0) listeners.splice(i, 1)
      }
    },
  }
}
